import { useState } from 'react';
import { usePredefinedRegions } from '@/hooks/useAnnotations';
import { getImportanceBadgeClasses } from '@/utils/colors';
import { Button, Spinner } from '@/components/ui';
import { XMarkIcon, MagnifyingGlassIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import { cn } from '@/utils/cn';
import { getResolvedRegion, type PredefinedRegion } from '@/services/annotationApi';

interface PredefinedRegionSelectorProps {
  onSelect: (region: PredefinedRegion) => void;
  onClose: () => void;
  templateHandedness?: 'right' | 'left' | 'ambidextrous';
  videoId?: string; // Video ID for functional region resolution via auto-detected lead side
}

/**
 * Helper to derive region_type from region data
 * Backend doesn't send this, so we compute it from the region name
 */
function getRegionType(region: PredefinedRegion): string {
  const nameLower = region.name.toLowerCase();

  // Functional regions (Lead/Trail)
  if (region.functional) {
    if (nameLower.includes('arm')) return 'arms';
    if (nameLower.includes('leg')) return 'legs';
    return 'other';
  }

  // Anatomical regions (Left/Right)
  if (nameLower.includes('arm')) return 'arms';
  if (nameLower.includes('leg')) return 'legs';
  if (nameLower.includes('torso') || nameLower.includes('body')) return 'torso';
  return 'other';
}

/**
 * Convert snake_case landmark name to human-readable Title Case
 * e.g. "left_shoulder" → "Left Shoulder"
 */
function formatLandmarkName(name: string): string {
  return name
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Classify a landmark index as "core" or "detail".
 *
 * Core:   nose (0), ears (7-8), shoulders/elbows/wrists (11-16),
 *         hips/knees/ankles (23-28)
 * Detail: eye corners (1-6), mouth (9-10), pinky (17-18), index (19-20),
 *         thumb (21-22), heel (29-30), foot_index (31-32)
 */
function getLandmarkCategory(index: number): 'core' | 'detail' {
  // Eye detail landmarks (inner/outer corners) and mouth
  if (index >= 1 && index <= 6) return 'detail';
  if (index === 9 || index === 10) return 'detail';
  // Hand detail landmarks
  if (index >= 17 && index <= 22) return 'detail';
  // Foot detail landmarks
  if (index >= 29 && index <= 32) return 'detail';
  return 'core';
}

/** Returns true if any detail landmarks (hand/foot detail) are present in the list */
function hasDetailLandmarks(indices: number[]): boolean {
  return indices.some((i) => getLandmarkCategory(i) === 'detail');
}

// =====================================================================
// Landmark Customization Panel
// =====================================================================

interface LandmarkCustomizationPanelProps {
  region: PredefinedRegion;
  onBack: () => void;
  onConfirm: (filteredRegion: PredefinedRegion) => void;
}

function LandmarkCustomizationPanel({
  region,
  onBack,
  onConfirm,
}: LandmarkCustomizationPanelProps) {
  const landmarks = region.landmarks || [];
  const landmarkNames = region.landmark_names || [];

  // Detail landmarks start UNCHECKED by default — they often have low
  // detection accuracy (pinky/index/thumb on COCO-17, heel/toe on some
  // detectors) and produce validation warnings. Core landmarks are always
  // checked. Coaches can opt-in to detail landmarks if they need them.
  const [checkedIndices, setCheckedIndices] = useState<Set<number>>(
    new Set(landmarks.filter((idx) => getLandmarkCategory(idx) === 'core'))
  );

  const toggleLandmark = (index: number) => {
    setCheckedIndices((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const selectAll = () => setCheckedIndices(new Set(landmarks));

  const deselectDetail = () => {
    setCheckedIndices((prev) => {
      const next = new Set(prev);
      landmarks.forEach((idx) => {
        if (getLandmarkCategory(idx) === 'detail') {
          next.delete(idx);
        }
      });
      return next;
    });
  };

  const handleConfirm = () => {
    const filtered = landmarks.filter((idx) => checkedIndices.has(idx));
    // Build matching landmark_names array aligned with filtered indices
    const filteredNames = filtered.map((idx) => {
      const pos = landmarks.indexOf(idx);
      return pos >= 0 && pos < landmarkNames.length ? landmarkNames[pos] : String(idx);
    });

    onConfirm({
      ...region,
      landmarks: filtered,
      landmark_names: filteredNames,
    });
  };

  // Partition into core and detail groups for display
  const coreLandmarks = landmarks.filter((idx) => getLandmarkCategory(idx) === 'core');
  const detailLandmarks = landmarks.filter((idx) => getLandmarkCategory(idx) === 'detail');

  const checkedCount = checkedIndices.size;
  const canConfirm = checkedCount > 0;
  const showDetailSection = detailLandmarks.length > 0;

  return (
    <div className="flex flex-col h-full">
      {/* Panel header */}
      <div className="flex items-center gap-3 p-6 border-b border-border-subtle">
        <button
          onClick={onBack}
          className="p-1.5 rounded-lg text-text-secondary hover:text-text-primary hover:bg-dark-elevated-2 transition-colors"
          aria-label="Back to region list"
        >
          <ArrowLeftIcon className="h-5 w-5" />
        </button>
        <div>
          <h3 className="text-lg font-semibold text-text-primary">{region.name}</h3>
          <p className="text-sm text-text-secondary">{region.description}</p>
        </div>
      </div>

      {/* Quick actions */}
      <div className="flex items-center gap-2 px-6 py-2.5 border-b border-border-subtle">
        <button
          onClick={selectAll}
          className="text-xs px-2.5 py-1 rounded text-text-muted hover:text-text-primary transition-colors"
        >
          Include all
        </button>
        <span className="text-border-subtle">|</span>
        <button
          onClick={deselectDetail}
          className="text-xs px-2.5 py-1 rounded text-text-muted hover:text-text-primary transition-colors"
        >
          Primary only
        </button>
        <span className="ml-auto text-xs font-medium text-text-secondary tabular-nums">
          {checkedCount} selected
        </span>
      </div>

      {/* Scrollable landmark list */}
      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        {/* Core landmarks — always-on by default */}
        {coreLandmarks.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="inline-block w-2 h-2 rounded-full bg-emerald-400 flex-shrink-0" />
              <h4 className="text-xs font-semibold text-text-secondary uppercase tracking-wider">
                Primary tracking points
              </h4>
            </div>
            <div className="grid grid-cols-2 gap-1.5">
              {coreLandmarks.map((idx) => {
                const pos = landmarks.indexOf(idx);
                const rawName = pos >= 0 && pos < landmarkNames.length ? landmarkNames[pos] : `Landmark ${idx}`;
                const displayName = formatLandmarkName(rawName);
                const checked = checkedIndices.has(idx);

                return (
                  <label
                    key={idx}
                    className={cn(
                      'flex items-center gap-3 px-3 py-2.5 rounded-lg border cursor-pointer transition-all select-none',
                      checked
                        ? 'border-emerald-500/30 bg-emerald-500/5'
                        : 'border-border-subtle bg-dark-elevated-1 opacity-50'
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleLandmark(idx)}
                      className="w-5 h-5 rounded accent-emerald-400 flex-shrink-0 cursor-pointer"
                    />
                    <span className={cn(
                      'text-sm leading-tight',
                      checked ? 'text-text-primary' : 'text-text-muted'
                    )}>
                      {displayName}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        )}

        {/* Detail landmarks — off by default, opt-in */}
        {showDetailSection && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="inline-block w-2 h-2 rounded-full bg-amber-400 flex-shrink-0" />
              <h4 className="text-xs font-semibold text-text-secondary uppercase tracking-wider">
                Optional detail points
              </h4>
              <span className="text-[10px] text-text-muted ml-auto">
                Lower detection accuracy
              </span>
            </div>
            <div className="grid grid-cols-2 gap-1.5">
              {detailLandmarks.map((idx) => {
                const pos = landmarks.indexOf(idx);
                const rawName = pos >= 0 && pos < landmarkNames.length ? landmarkNames[pos] : `Landmark ${idx}`;
                const displayName = formatLandmarkName(rawName);
                const checked = checkedIndices.has(idx);

                return (
                  <label
                    key={idx}
                    className={cn(
                      'flex items-center gap-3 px-3 py-2.5 rounded-lg border cursor-pointer transition-all select-none',
                      checked
                        ? 'border-amber-500/30 bg-amber-500/5'
                        : 'border-border-subtle bg-dark-elevated-1 opacity-40'
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleLandmark(idx)}
                      className="w-5 h-5 rounded accent-amber-400 flex-shrink-0 cursor-pointer"
                    />
                    <span className={cn(
                      'text-sm leading-tight',
                      checked ? 'text-text-primary' : 'text-text-muted'
                    )}>
                      {displayName}
                    </span>
                  </label>
                );
              })}
            </div>
            <p className="text-xs text-text-muted mt-2 leading-relaxed">
              These points (fingers, toes) are harder for the camera to track consistently.
              Only include them if your analysis specifically needs them.
            </p>
          </div>
        )}
      </div>

      {/* Footer actions */}
      <div className="px-6 py-4 border-t border-border-subtle flex items-center justify-between gap-3">
        <button
          onClick={onBack}
          className="text-sm text-text-secondary hover:text-text-primary transition-colors"
        >
          &larr; Back to regions
        </button>
        <Button
          variant="primary"
          onClick={handleConfirm}
          disabled={!canConfirm}
          title={!canConfirm ? 'Select at least one landmark' : undefined}
        >
          Add {checkedCount} landmark{checkedCount !== 1 ? 's' : ''}
        </Button>
      </div>
    </div>
  );
}

// =====================================================================
// Main PredefinedRegionSelector
// =====================================================================

/**
 * PredefinedRegionSelector Component
 *
 * Modal that displays a grid of predefined body regions for quick selection.
 * Allows searching/filtering regions by name or description.
 *
 * After selecting a region card the coach sees a "Customize Landmarks" panel
 * where they can deselect individual landmarks (e.g. hand detail landmarks that
 * ViTPose doesn't detect) before the annotation is created.
 */
export function PredefinedRegionSelector({
  onSelect,
  onClose,
  templateHandedness: _templateHandedness = 'ambidextrous',  // Default to ambidextrous - lead side is auto-detected
  videoId,
}: PredefinedRegionSelectorProps) {
  const { data: regions, isLoading, error } = usePredefinedRegions();
  const [searchTerm, setSearchTerm] = useState('');
  // Track which region is currently being resolved (by region id) so we can show per-card loading
  const [resolvingRegionId, setResolvingRegionId] = useState<string | null>(null);
  const [resolveError, setResolveError] = useState<string | null>(null);
  // When non-null, the region grid is replaced by the customization panel
  const [pendingRegion, setPendingRegion] = useState<PredefinedRegion | null>(null);

  /**
   * Handle region card click.
   * - For anatomical (non-functional) regions: resolve immediately then show customization.
   * - For functional regions: call backend to auto-detect lead side from video pose data,
   *   then show customization panel with the resolved landmark set.
   */
  const handleRegionClick = async (region: PredefinedRegion) => {
    setResolveError(null);

    if (!region.functional) {
      // Anatomical region — no resolution needed, go straight to customization
      setPendingRegion(region);
      return;
    }

    if (!videoId) {
      // No videoId available — fall back to static defaults with a warning
      console.warn(
        'PredefinedRegionSelector: videoId not provided for functional region resolution. ' +
        'Using static defaults which may have wrong lead side.'
      );
      setPendingRegion({ ...region, functional_region: region.id } as PredefinedRegion & { functional_region: string });
      return;
    }

    // Resolve functional region against video's actual pose data
    setResolvingRegionId(region.id);
    try {
      const resolved = await getResolvedRegion(region.id, videoId);
      // Attach functional_region so the annotation data can store it for runtime re-resolution
      setPendingRegion({ ...resolved, functional_region: region.id } as PredefinedRegion & { functional_region: string });
    } catch (err) {
      console.error('Failed to resolve functional region:', err);
      setResolveError(
        `Could not auto-detect lead side for "${region.name}". ` +
        'Please try again or use an anatomical region (Left Arm / Right Arm) instead.'
      );
    } finally {
      setResolvingRegionId(null);
    }
  };

  /** Called from customization panel — applies filtered landmarks and fires onSelect */
  const handleCustomizationConfirm = (filteredRegion: PredefinedRegion) => {
    onSelect(filteredRegion);
  };

  /** Return to the region grid from the customization panel */
  const handleCustomizationBack = () => {
    setPendingRegion(null);
  };

  // Filter regions based on search term
  const filteredRegions = regions?.filter(
    (r) => {
      const regionType = getRegionType(r);
      return (
        r.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        r.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        regionType.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
  );

  // Separate anatomical and functional regions
  const anatomicalRegions = filteredRegions?.filter(r => !r.functional);
  const functionalRegions = filteredRegions?.filter(r => r.functional);

  // Group anatomical regions by type
  const groupedAnatomical = anatomicalRegions?.reduce((acc, region) => {
    const type = getRegionType(region);
    if (!acc[type]) {
      acc[type] = [];
    }
    acc[type].push(region);
    return acc;
  }, {} as Record<string, PredefinedRegion[]>);

  // Group functional regions by type
  const groupedFunctional = functionalRegions?.reduce((acc, region) => {
    const type = getRegionType(region);
    if (!acc[type]) {
      acc[type] = [];
    }
    acc[type].push(region);
    return acc;
  }, {} as Record<string, PredefinedRegion[]>);

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-dark-card rounded-lg shadow-xl max-w-4xl w-full max-h-[85vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Customization panel replaces the region grid */}
        {pendingRegion ? (
          <LandmarkCustomizationPanel
            region={pendingRegion}
            onBack={handleCustomizationBack}
            onConfirm={handleCustomizationConfirm}
          />
        ) : (
          <>
            {/* Header */}
            <div className="flex justify-between items-center p-6 border-b border-border-subtle">
              <div>
                <h2 className="text-2xl font-semibold text-text-primary">Quick Select Body Region</h2>
                <p className="text-sm text-text-secondary mt-1">
                  Choose from predefined anatomical regions with pre-mapped landmarks
                </p>
              </div>
              <button
                onClick={onClose}
                className="text-text-secondary hover:text-text-primary transition-colors p-2 rounded-lg hover:bg-dark-elevated-2"
                aria-label="Close"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Search Bar */}
            <div className="p-6 border-b border-border-subtle">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-text-muted" />
                <input
                  type="text"
                  placeholder="Search regions (e.g., 'left arm', 'knee', 'torso')..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-border-subtle bg-dark-elevated-1 text-text-primary placeholder:text-text-muted rounded-lg focus:ring-2 focus:ring-accent/50 focus:border-accent"
                />
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {isLoading && (
                <div className="flex items-center justify-center py-12">
                  <Spinner className="h-8 w-8 text-accent" />
                  <span className="ml-3 text-text-secondary">Loading regions...</span>
                </div>
              )}

              {error && (
                <div className="bg-danger/10 border border-danger/30 rounded-lg p-4 text-danger">
                  Failed to load predefined regions. Please try again.
                </div>
              )}

              {!isLoading && !error && filteredRegions && filteredRegions.length === 0 && (
                <div className="text-center py-12 text-text-secondary">
                  <p className="text-lg">No regions found matching "{searchTerm}"</p>
                  <p className="text-sm mt-2">Try a different search term</p>
                </div>
              )}

              {resolveError && (
                <div className="mb-4 bg-danger/10 border border-danger/30 rounded-lg p-4 text-danger text-sm">
                  {resolveError}
                </div>
              )}

              {!isLoading && !error && (groupedAnatomical || groupedFunctional) && (
                <div className="space-y-8">
                  {/* Anatomical Regions Section */}
                  {groupedAnatomical && Object.keys(groupedAnatomical).length > 0 && (
                    <div>
                      <div className="mb-4 pb-3 border-b border-border-subtle">
                        <h2 className="text-lg font-bold text-text-primary">Anatomical Regions (Left/Right)</h2>
                        <p className="text-xs text-text-secondary mt-1">
                          These labels swap sides when comparing opposite-handed athletes
                        </p>
                      </div>
                      <div className="space-y-6">
                        {Object.entries(groupedAnatomical).map(([type, typeRegions]) => (
                          <div key={type}>
                            <h3 className="text-sm font-semibold text-text-secondary uppercase tracking-wide mb-3">
                              {type.replace('_', ' ')}
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                              {typeRegions.map((region) => (
                                <RegionCard
                                  key={region.id}
                                  region={region}
                                  onSelect={() => handleRegionClick(region)}
                                  isLoading={false}
                                />
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Functional Regions Section */}
                  {groupedFunctional && Object.keys(groupedFunctional).length > 0 && (
                    <div>
                      <div className="mb-4 pb-3 border-b border-border-subtle">
                        <h2 className="text-lg font-bold text-text-primary">Functional Regions (Lead/Trail)</h2>
                        <p className="text-xs text-text-secondary mt-1">
                          These labels stay constant regardless of handedness
                        </p>
                        <div className="mt-2 bg-info/10 border border-info/30 rounded p-2">
                          <p className="text-xs text-info">
                            Lead side is <strong>auto-detected</strong> from the video's pose data when you select a region
                          </p>
                        </div>
                      </div>
                      <div className="space-y-6">
                        {Object.entries(groupedFunctional).map(([type, typeRegions]) => (
                          <div key={type}>
                            <h3 className="text-sm font-semibold text-text-secondary uppercase tracking-wide mb-3">
                              {type.replace('_', ' ')}
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                              {typeRegions.map((region) => (
                                <RegionCard
                                  key={region.id}
                                  region={region}
                                  onSelect={() => handleRegionClick(region)}
                                  isLoading={resolvingRegionId === region.id}
                                />
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-border-subtle bg-dark-elevated-1">
              <div className="flex items-center justify-between">
                <p className="text-sm text-text-secondary">
                  {filteredRegions?.length || 0} region{filteredRegions?.length !== 1 ? 's' : ''} available
                </p>
                <Button variant="outline" onClick={onClose}>
                  Cancel
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// =====================================================================
// RegionCard Component
// =====================================================================

/**
 * RegionCard Component
 *
 * Individual card for each predefined region
 */
interface RegionCardProps {
  region: PredefinedRegion;
  onSelect: () => void;
  isLoading?: boolean;
}

function RegionCard({ region, onSelect, isLoading = false }: RegionCardProps) {
  const landmarks = region.landmarks || [];
  const showDetailBadge = hasDetailLandmarks(landmarks);

  return (
    <button
      onClick={onSelect}
      disabled={isLoading}
      className={cn(
        'border rounded-lg p-4 text-left transition-all relative',
        'hover:bg-dark-elevated-2 hover:border-accent hover:shadow-md',
        'focus:outline-none focus:ring-2 focus:ring-accent/50 focus:ring-offset-2',
        'border-border-subtle bg-dark-elevated-1',
        isLoading && 'opacity-70 cursor-wait'
      )}
    >
      <div className="flex items-start justify-between mb-2">
        <h3 className="font-semibold text-text-primary text-base">{region.name}</h3>
        <div className="flex items-center gap-1.5">
          {isLoading && <Spinner className="h-4 w-4 text-accent" />}
          {region.side && !isLoading && (
            <span className="text-xs px-2 py-1 rounded bg-dark-elevated-2 text-text-secondary capitalize">
              {region.side}
            </span>
          )}
        </div>
      </div>

      <p className="text-sm text-text-secondary mb-3 line-clamp-2">{region.description}</p>

      <div className="flex flex-wrap items-center gap-2">
        <span className={cn('text-xs px-2 py-1 rounded border', getImportanceBadgeClasses(region.default_importance || 'medium'))}>
          {region.default_importance || 'medium'}
        </span>
        <span className="text-xs text-text-muted">
          {landmarks.length} landmarks
        </span>
        {showDetailBadge && (
          <span className="text-xs px-2 py-1 rounded bg-yellow-500/10 border border-yellow-500/30 text-yellow-400">
            has detail
          </span>
        )}
      </div>

      {/* Show landmark names preview (or auto-detect note for functional regions) */}
      <div className="mt-2 pt-2 border-t border-border-subtle">
        {region.functional ? (
          <p className="text-xs text-info/80 italic">
            {isLoading ? 'Detecting lead side from video...' : 'Auto-detected from video pose data'}
          </p>
        ) : region.landmark_names && region.landmark_names.length > 0 ? (
          <p className="text-xs text-text-muted line-clamp-1" title={region.landmark_names.join(', ')}>
            {region.landmark_names.slice(0, 3).join(', ')}
            {region.landmark_names.length > 3 && ` +${region.landmark_names.length - 3}...`}
          </p>
        ) : null}
      </div>
    </button>
  );
}

export default PredefinedRegionSelector;
